import React, { useEffect, useState } from 'react';
import useAuth from './hooks/useAuth';

import {
  BrowserRouter as Router,
  Routes,
  Route,
  NavLink,
  useLocation,
  useNavigate,
  Link,
  Navigate,
} from 'react-router-dom';

import SalesPage from './pages/SalesPage';
import ProductPage from './pages/ProductPage';
import AdvancedProductPage from './pages/AdvancedProductPage';
import ReceiptHistoryPage from './pages/ReceiptHistoryPage';
import PreordersPage from './pages/PreordersPage';
import ReportingPage from './pages/ReportingPage';
import CategoriesPage from './pages/CategoriesPage';
import TaxRatesPage from './pages/TaxRatesPage';
import UserManagementPage from './pages/UserManagementPage';
import CustomersPage from './pages/CustomersPage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrivateRoute from './components/PrivateRoute';
import PosLiteShell, {
  LiteMorePlaceholder,
  LiteProductsPlaceholder,
} from './pages/PosLiteShell';
import LiteHome from './pages/lite/LiteHome';
import LitePreorders from './pages/lite/LitePreorders';
import LiteSales from './pages/lite/LiteSales';
import LiteProductLookup from './pages/lite/LiteProductLookup';
import PreorderPage from './pages/PreorderPage';
import WebhookOutboxPage from './pages/WebhookOutboxPage';
import api from './api/axios';
import { formatOutboxBadgeCount, shouldShowOutboxBadge } from './utils/outboxBadge';
import { isPublicRoute } from './utils/publicRoutes';
import InstallPrompt from './components/InstallPrompt';

function PageWrapper({ children, layoutMode, variant = 'default' }) {
  if (variant === 'sales') {
    return <main className="flex-1 overflow-hidden bg-slate-950">{children}</main>;
  }

  if (variant === 'bare') {
    return <main className="flex-1 overflow-auto bg-slate-950">{children}</main>;
  }

  const containerWidth =
    layoutMode === 'contained' ? 'mx-auto w-full max-w-7xl' : 'w-full';

  return (
    <main className="flex-1 overflow-auto bg-slate-950">
      <div className={`min-h-full px-6 py-6 ${containerWidth}`}>{children}</div>
    </main>
  );
}

function AppWrapper() {
  const isCustomerRoute = (pathname) =>
    pathname.startsWith('/preorders/local') || pathname.startsWith('/preorder');
  const [layoutMode, setLayoutMode] = useState('full');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasDismissedLitePrompt, setHasDismissedLitePrompt] = useState(() =>
    localStorage.getItem('posLitePromptDismissed') === '1'
  );
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);
  const [showLitePrompt, setShowLitePrompt] = useState(false);
  const { user, isAdmin, logout, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isLiteRoute = location.pathname.startsWith('/lite');

  useEffect(() => {
    const isPublicPath =
      ['/login', '/reset-password'].includes(location.pathname) ||
      isCustomerRoute(location.pathname);

    if (!token && !isPublicPath) {
      navigate('/login');
    }
  }, [token, location.pathname, navigate]);

  useEffect(() => {
    if (location.pathname !== '/') {
      setIsSearchOpen(false);
      setSearchTerm('');
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isSearchOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsSearchOpen(false);
        setSearchTerm('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isCustomerRoute(location.pathname)) {
      setShowIosHint(false);
      return;
    }
    if (/iphone|ipad|ipod/i.test(window.navigator.userAgent)) {
      setShowIosHint(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isiOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    setShowIosHint(isiOS);
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      if (isCustomerRoute(location.pathname)) {
        setShowInstallPrompt(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () =>
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, [location.pathname]);

  useEffect(() => {
    if (!isCustomerRoute(location.pathname)) {
      setShowInstallPrompt(false);
      return;
    }
    if (deferredPrompt) {
      setShowInstallPrompt(true);
    }
  }, [location.pathname, deferredPrompt]);

  useEffect(() => {
    const updatePromptVisibility = () => {
      const isSmallScreen = window.innerWidth < 768;
      if (!token || isLiteRoute || hasDismissedLitePrompt) {
        setShowLitePrompt(false);
        return;
      }
      setShowLitePrompt(isSmallScreen);
    };

    updatePromptVisibility();
    window.addEventListener('resize', updatePromptVisibility);
    return () => window.removeEventListener('resize', updatePromptVisibility);
  }, [token, isLiteRoute, hasDismissedLitePrompt]);

  const baseMenuItems = [
    { to: '/', label: 'Sales', exact: true },
    { to: '/products', label: 'Products' },
    { to: '/receipts', label: 'History' },
    { to: '/preorders', label: 'Pre-Orders' },
    { to: '/reports', label: 'Reports' },
  ];

  const menuItems = isAdmin
    ? [
        ...baseMenuItems,
        { to: '/customers', label: 'Customers' },
        { to: '/categories', label: 'Categories' },
        { to: '/taxrates', label: 'Tax Rates' },
        { to: '/team', label: 'Team' },
        { to: '/settings', label: 'Settings' },
      ]
    : baseMenuItems;

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  const toggleSearch = () => {
    setIsSearchOpen((prev) => {
      if (prev) {
        setSearchTerm('');
      }
      return !prev;
    });
  };

  const handleSearchChange = (nextValue) => {
    setSearchTerm(nextValue);
    if (!isSearchOpen) {
      setIsSearchOpen(true);
    }
  };

  const handleLayoutToggle = () =>
    setLayoutMode((prev) => (prev === 'full' ? 'contained' : 'full'));

  const handleDismissLitePrompt = () => {
    localStorage.setItem('posLitePromptDismissed', '1');
    setHasDismissedLitePrompt(true);
    setShowLitePrompt(false);
  };

  const handleInstallClick = () => {
    if (!deferredPrompt) {
      return;
    }
    deferredPrompt.prompt();
    deferredPrompt.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === 'accepted') {
        setShowInstallPrompt(false);
        setDeferredPrompt(null);
      } else {
        setShowInstallPrompt(false);
      }
    });
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-slate-950">
      {token && !isLiteRoute && (
        <>
          <AppHeader
            appName="Ofoodi POS"
            isSearchOpen={isSearchOpen}
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onSearchClear={() => setSearchTerm('')}
            onSearchToggle={toggleSearch}
            onMenuToggle={toggleMenu}
            onLogout={logout}
            user={user}
          />
          <SideMenu
            isOpen={isMenuOpen}
            onClose={() => setIsMenuOpen(false)}
            menuItems={menuItems}
            layoutMode={layoutMode}
            onLayoutToggle={handleLayoutToggle}
            onLogout={logout}
            user={user}
          />
        </>
      )}

      <div className={`flex-1 flex flex-col ${token && !isLiteRoute ? 'pt-16' : ''}`}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/lite/*"
            element={
              <PrivateRoute>
                <PosLiteShell />
              </PrivateRoute>
            }
          >
            <Route index element={<LiteHome />} />
            <Route path="preorders" element={<LitePreorders />} />
            <Route path="sales" element={<LiteSales />} />
            <Route path="products" element={<LiteProductLookup />} />
            <Route path="more" element={<LiteMorePlaceholder />} />
          </Route>
          <Route
            path="/"
            element={
              <PrivateRoute>
                <PageWrapper layoutMode={layoutMode} variant="sales">
                  <SalesPage
                    searchTerm={searchTerm}
                    onSearchTermChange={setSearchTerm}
                  />
                </PageWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/products"
            element={
              <PrivateRoute>
                <PageWrapper layoutMode={layoutMode} variant="sales">
                  <ProductPage />
                </PageWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/products/advanced"
            element={
              <PrivateRoute>
                <PageWrapper layoutMode={layoutMode}>
                  <AdvancedProductPage />
                </PageWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/receipts"
            element={
              <PrivateRoute>
                <PageWrapper layoutMode={layoutMode} variant="bare">
                  <ReceiptHistoryPage />
                </PageWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/preorders"
            element={
              <PrivateRoute>
                <PageWrapper layoutMode={layoutMode} variant="bare">
                  <PreordersPage />
                </PageWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/preorders/new"
            element={
              <PrivateRoute>
                <PageWrapper layoutMode={layoutMode} variant="sales">
                  <PreorderPage />
                </PageWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <PrivateRoute>
                <PageWrapper layoutMode={layoutMode} variant="bare">
                  <ReportingPage />
                </PageWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <PrivateRoute requireAdmin>
                <PageWrapper layoutMode={layoutMode} variant="bare">
                  <CustomersPage />
                </PageWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/categories"
            element={
              <PrivateRoute requireAdmin>
                <PageWrapper layoutMode={layoutMode} variant="bare">
                  <CategoriesPage />
                </PageWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/taxrates"
            element={
              <PrivateRoute requireAdmin>
                <PageWrapper layoutMode={layoutMode} variant="bare">
                  <TaxRatesPage />
                </PageWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/team"
            element={
              <PrivateRoute requireAdmin>
                <PageWrapper layoutMode={layoutMode} variant="bare">
                  <UserManagementPage />
                </PageWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute requireAdmin>
                <PageWrapper layoutMode={layoutMode} variant="bare">
                  <SettingsPage />
                </PageWrapper>
              </PrivateRoute>
            }
          />
        </Routes>
      </div>

      {showLitePrompt && <LitePromptBanner onDismiss={handleDismissLitePrompt} />}
      {showInstallPrompt && (
        <button
          type="button"
          onClick={handleInstallClick}
          className="fixed bottom-4 right-4 z-50 rounded-full border border-emerald-400 bg-emerald-500/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-950 shadow-lg shadow-emerald-500/30 transition hover:border-emerald-300"
        >
          Install Ofoodi Local
        </button>
      )}
      {showIosHint && isCustomerRoute(location.pathname) && (
        <div className="fixed bottom-20 right-4 z-50 max-w-xs space-y-2 rounded-2xl border border-slate-700 bg-slate-950/95 px-4 py-3 text-xs text-slate-200 shadow-[0_20px_40px_-30px_rgba(0,0,0,0.9)] backdrop-blur">
          <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Add to Home Screen</p>
          <p>On Safari for iOS use the Share menu and choose “Add to Home Screen.”</p>
          <button
            type="button"
            onClick={() => setShowIosHint(false)}
            className="text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-300 hover:text-emerald-100"
          >
            Got it
          </button>
        </div>
      )}
    </div>
  );
}

function AppHeader({
  appName,
  isSearchOpen,
  searchTerm,
  onSearchChange,
  onSearchClear,
  onSearchToggle,
  onMenuToggle,
  onLogout,
  user,
}) {
  const inputRef = React.useRef(null);

  useEffect(() => {
    if (!isSearchOpen || !inputRef.current) {
      return;
    }

    const node = inputRef.current;
    requestAnimationFrame(() => {
      node.focus({ preventScroll: true });
      const length = node.value.length;
      node.setSelectionRange(length, length);
    });
  }, [isSearchOpen]);

  const handleClearClick = () => {
    onSearchClear();
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const headerClasses =
    'fixed top-0 inset-x-0 z-50 h-16 bg-slate-900 text-slate-100 shadow-[0_6px_24px_rgba(15,23,42,0.45)]';

  return (
    <header className={headerClasses}>
      <div className="relative flex h-full items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onMenuToggle}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 bg-slate-800/60 transition hover:border-emerald-400 hover:text-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-400/80"
            aria-label="Open main menu"
          >
            <IconMenu className="h-5 w-5" />
          </button>
          <div className="flex flex-col leading-tight">
            <span className="text-lg font-semibold tracking-wide">{appName}</span>
            <span className="text-xs uppercase tracking-[0.25em] text-slate-400">
              Point of Sale
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden sm:flex flex-col items-end leading-tight">
            <span className="text-sm font-medium text-white">{user?.username}</span>
            <span className="text-xs uppercase tracking-wide text-slate-400">
              {user?.role}
            </span>
          </div>

          <button
            type="button"
            onClick={onSearchToggle}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 bg-slate-800/60 transition hover:border-emerald-400 hover:text-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-400/80"
            aria-label={isSearchOpen ? 'Close search' : 'Open search'}
          >
            {isSearchOpen ? (
              <IconClose className="h-5 w-5" />
            ) : (
              <IconSearch className="h-5 w-5" />
            )}
          </button>

          <button
            type="button"
            onClick={onLogout}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 bg-slate-800/60 transition hover:border-red-400 hover:text-red-200 focus:outline-none focus:ring-2 focus:ring-red-400/80"
            aria-label="Logout"
          >
            <IconExit className="h-5 w-5" />
          </button>
        </div>

        {isSearchOpen && (
          <div className="absolute inset-0 z-10 flex items-center gap-3 bg-slate-900/95 px-4">
            <IconSearch className="h-5 w-5 text-slate-400" />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => onSearchChange(event.target.value)}
              ref={inputRef}
              className="flex-1 border-0 bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
              placeholder="Search products by name, SKU, or barcode"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={handleClearClick}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-300 transition hover:bg-slate-800"
                aria-label="Clear search"
              >
                <IconClose className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={onSearchToggle}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-300 transition hover:bg-slate-800"
              aria-label="Close search overlay"
            >
              <IconClose className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

function SideMenu({
  isOpen,
  onClose,
  menuItems,
  layoutMode,
  onLayoutToggle,
  onLogout,
  user,
}) {
  const closeButtonRef = React.useRef(null);

  useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isOpen]);

  return (
    <>
      <div
        className={`fixed inset-0 z-30 bg-slate-950/70 transition-opacity ${
          isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
        aria-hidden={!isOpen}
      ></div>

      <aside
        className={`fixed inset-y-0 left-0 z-40 w-72 transform border-r border-slate-800 bg-slate-900/95 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)] transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-hidden={!isOpen}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-slate-800 px-4 py-4">
            <span className="text-lg font-semibold text-slate-100">Menu</span>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-300 transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-brandGreen/40"
              aria-label="Close menu"
              ref={closeButtonRef}
            >
              <IconClose className="h-5 w-5" />
            </button>
          </div>

          <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-4">
            {menuItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                onClick={onClose}
                className={({ isActive }) =>
                  [
                    'block rounded-xl px-3 py-2 text-sm font-semibold uppercase tracking-wide transition',
                    isActive
                      ? 'bg-brandGreen text-white shadow'
                      : 'text-slate-300 hover:bg-slate-800',
                  ].join(' ')
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="space-y-3 border-t border-slate-800 px-4 py-4 text-sm text-slate-400">
            <button
              type="button"
              onClick={onLayoutToggle}
              className="flex w-full items-center justify-between rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 transition hover:border-slate-600 hover:bg-slate-900"
            >
              <span>Layout width</span>
              <span className="font-semibold text-slate-100">
                {layoutMode === 'full' ? 'Full' : 'Contained'}
              </span>
            </button>

            <div className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2">
              <div>
                <p className="text-sm font-semibold text-slate-100">{user?.username}</p>
                <p className="text-xs uppercase tracking-wide text-slate-500">{user?.role}</p>
              </div>
              <button
                type="button"
                onClick={onLogout}
                className="inline-flex items-center gap-1 rounded-full border border-rose-500/60 bg-rose-900/20 px-3 py-1 text-sm font-semibold text-rose-200 transition hover:bg-rose-500/30"
              >
                <IconExit className="h-4 w-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

function IconMenu({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function IconSearch({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="20" y1="20" x2="16.65" y2="16.65" />
    </svg>
  );
}

function IconClose({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function IconExit({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M14 7l5 5-5 5" />
      <path d="M19 12H9" />
      <path d="M5 4v16" />
    </svg>
  );
}

function LitePromptBanner({ onDismiss }) {
  return (
    <div className="fixed bottom-4 left-4 right-4 z-40 max-w-2xl rounded-2xl border border-emerald-500/40 bg-slate-950/95 px-4 py-4 text-white shadow-[0_20px_40px_-20px_rgba(0,0,0,0.6)] backdrop-blur sm:left-8 sm:right-auto sm:px-6">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-600/15 text-emerald-200">
          <IconMenu className="h-5 w-5" />
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold leading-snug">Switch to POS Lite</p>
            <button
              type="button"
              onClick={onDismiss}
              className="rounded-full p-1 text-slate-400 transition hover:bg-slate-900 hover:text-white"
              aria-label="Dismiss"
            >
              <IconClose className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-slate-300">
            You are on a small screen. POS Lite is optimized for thumb use with a mobile shell.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/lite"
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-900/40 transition hover:bg-emerald-500"
            >
              Open POS Lite
            </Link>
            <button
              type="button"
              onClick={onDismiss}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-slate-500"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AppWrapper />
    </Router>
  );
}
